import { createStyles, FormHelperText, Typography, withStyles, WithStyles } from '@material-ui/core'
import InputAdornment from '@material-ui/core/InputAdornment'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import React, { useEffect, useState } from 'react'

import { clamp } from 'src/commons/helpers'
import { PREFIXED_CURRENCY_SYMBOLS } from 'src/commons/i18nEnums'

import { DebouncedTextField } from './DebouncedTextField'

interface IProps {
  bufferNeedUpdate?: boolean
  currency?: string
  disabled?: boolean
  error?: boolean
  helperText?: string
  id?: string
  inputProps?: { [key: string]: string }
  inputType?: 'outlined' | 'text'
  label?: string
  margin?: 'none' | 'dense' | undefined
  max?: number | undefined
  min?: number | undefined
  onChange: (value: number) => void
  placeholder?: string
  unit?: string
  value: number | undefined
}

const styles = createStyles({
  currencyInput: {
    maxWidth: 100,
    textAlign: 'end',
  },
  currencyOutlinedInput: {
    textAlign: 'end',
    marginRight: '3px',
  },
  inputWidth: {
    width: '160px',
  },
})

const isCurrencyOrUnit = (isPrefixCurrency: boolean, currency?: string, unit?: string) => {
  if (isPrefixCurrency) {
    return undefined
  } else if (unit) {
    return unit
  } else {
    return currency
  }
}

const NumericInput = (props: IProps & WithStyles) => {
  const {
    value,
    onChange,
    currency = '',
    inputType = 'outlined',
    margin = 'dense',
    placeholder = '',
    bufferNeedUpdate = false,
    label = '',
    error,
    helperText,
    disabled,
    classes,
    unit,
    inputProps,
  } = props
  const [bufferValue, setBufferValue] = useState((value ?? 0).toString())

  useEffect(() => {
    setBufferValue((value ?? 0).toString())
  }, [value, bufferNeedUpdate])

  const isPrefixCurrency = PREFIXED_CURRENCY_SYMBOLS.includes(currency)

  if (inputType === 'outlined') {
    return (
      <div style={{ width: '160px', verticalAlign: 'bottom' }}>
        <OutlinedInput
          id={props.id}
          type={'number'}
          style={{ height: '35px', verticalAlign: 'bottom' }}
          disabled={disabled}
          value={bufferValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const parsedValue = clamp(parseFloat(e.target.value || '0'), props.min, props.max)

            setBufferValue(parsedValue.toString())
            onChange(parsedValue)
          }}
          inputProps={{
            ...inputProps,
            maxLength: 20,
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
              return e.currentTarget.select()
            },
            className: isPrefixCurrency ? undefined : classes.currencyOutlinedInput,
            min: props.min,
            max: props.max,
          }}
          startAdornment={isPrefixCurrency ? <InputAdornment position="start">{currency}</InputAdornment> : undefined}
          endAdornment={isCurrencyOrUnit(isPrefixCurrency, currency, unit)}
          labelWidth={0}
          margin={margin}
          error={error}
        />
        {helperText && (
          <FormHelperText className={classes.helper} error={error}>
            <Typography variant="caption" color={error ? 'error' : 'initial'}>
              {helperText}
            </Typography>
          </FormHelperText>
        )}
      </div>
    )
  }
  return (
    <DebouncedTextField
      id={props.id}
      type={'number'}
      disabled={disabled}
      value={bufferValue}
      onFinalChange={(e: string) => {
        const parsedValue = clamp(parseFloat(e || '0'), props.min, props.max)

        setBufferValue(parsedValue.toString())
        onChange(parsedValue)
      }}
      timeToWait={500}
      InputProps={{
        ...inputProps,
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          return e.currentTarget.select()
        },
        classes: {
          input: isPrefixCurrency ? undefined : classes.currencyInput,
        },
        startAdornment: isPrefixCurrency ? (
          <InputAdornment position="start">
            <span style={{ padding: '3px 0px 7px' }}>{currency}</span>
          </InputAdornment>
        ) : undefined,
        endAdornment: isCurrencyOrUnit(isPrefixCurrency, currency, unit),
      }}
      margin={margin}
      placeholder={placeholder}
      label={label}
      error={error}
      helperText={helperText}
    />
  )
}

export default withStyles(styles)(NumericInput)
